import dayjs from '@common/dayjs';
import {
  COMPARISON,
  DATE_FORMAT,
  DAY_GRANULARITY_MAX,
  DAYS_IN_MONTH,
  DAYS_IN_QUARTER,
  DAYS_IN_WEEK,
  GRAN,
  PRESET,
  WEEK_GRANULARITY_MAX,
} from '@common/dashboard/constants';

/*
 * Dashboard period logic
 * Dates as 'YYYY-MM-DD' strings
 *
 * PERIOD RULE:
 *   flows        → sum/average inside the period
 *   stocks       → value on the last day of the period
 *   comparisons  → vs. the previous period of the same length ("this month": the same days of the previous month, "this year": the same dates of the previous year)
 *   granularity  → hour (1 day) · day (≤ 62) · week (≤ 200) · month
 */

export {DATE_FORMAT};

export const PRESETS = [
  PRESET.TODAY,
  PRESET.YESTERDAY,
  PRESET.LAST_7,
  PRESET.LAST_30,
  PRESET.LAST_90,
  PRESET.THIS_MONTH,
  PRESET.THIS_YEAR,
];
export const DEFAULT_PRESET = PRESET.LAST_30;
export const CUSTOM_PRESET = PRESET.CUSTOM;

export function today() {
  return dayjs().startOf('day');
}

function toDay(value) {
  return dayjs(value, DATE_FORMAT).startOf('day');
}

function range(start, end) {
  return {start: start.format(DATE_FORMAT), end: end.format(DATE_FORMAT)};
}

export function presetRange(preset, now = today()) {
  switch (preset) {
    case PRESET.YESTERDAY: {
      const day = now.subtract(1, 'day');
      return range(day, day);
    }
    case PRESET.LAST_7:
      return range(now.subtract(DAYS_IN_WEEK - 1, 'days'), now);
    case PRESET.LAST_30:
      return range(now.subtract(DAYS_IN_MONTH - 1, 'days'), now);
    case PRESET.LAST_90:
      return range(now.subtract(DAYS_IN_QUARTER - 1, 'days'), now);
    case PRESET.THIS_MONTH:
      return range(now.startOf('month'), now);
    case PRESET.THIS_YEAR:
      return range(now.startOf('year'), now);
    default: // 'today'
      return range(now, now);
  }
}

/**
 * Custom range: both dates capped at today, then swapped if the start is after the end
 */
export function applyCustom(startValue, endValue, now = today()) {
  let start = toDay(startValue);
  let end = toDay(endValue);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  if (start.isAfter(now)) {
    start = now;
  }
  if (end.isAfter(now)) {
    end = now;
  }
  if (start.isAfter(end)) {
    [start, end] = [end, start];
  }
  return range(start, end);
}

export function granularity(days) {
  if (days === 1) {
    return GRAN.HOUR;
  }
  if (days <= DAY_GRANULARITY_MAX) {
    return GRAN.DAY;
  }
  if (days <= WEEK_GRANULARITY_MAX) {
    return GRAN.WEEK;
  }
  return GRAN.MONTH;
}

/**
 * Previous period for the comparisons
 *   thisMonth → the same days of the previous month (1–5 Oct → 1–5 Sep), capped at that month's last day
 *   thisYear  → the same dates of the previous year (1 Jan–5 Oct 2026 → 1 Jan–5 Oct 2025)
 *   others    → the days right before the start, with the same length
 */
function previousRange(start, end, days, preset) {
  if (preset === PRESET.THIS_MONTH) {
    return {prevStart: start.subtract(1, 'month'), prevEnd: end.subtract(1, 'month')};
  }
  if (preset === PRESET.THIS_YEAR) {
    return {prevStart: start.subtract(1, 'year'), prevEnd: end.subtract(1, 'year')};
  }
  return {prevStart: start.subtract(days, 'days'), prevEnd: start.subtract(1, 'day')};
}

function comparison(days, preset) {
  if (preset === PRESET.THIS_MONTH) {
    return COMPARISON.PREVIOUS_MONTH;
  }
  if (preset === PRESET.THIS_YEAR) {
    return COMPARISON.PREVIOUS_YEAR;
  }
  return days === 1 ? COMPARISON.PREVIOUS_DAY : COMPARISON.PREVIOUS_PERIOD;
}

export function buildPeriod(startValue, endValue, preset) {
  const start = toDay(startValue);
  const end = toDay(endValue);
  const days = end.diff(start, 'days') + 1;
  const {prevStart, prevEnd} = previousRange(start, end, days, preset);
  const gran = granularity(days);
  const apiParams = {
    start: start.format(DATE_FORMAT),
    end: end.format(DATE_FORMAT),
    prevStart: prevStart.format(DATE_FORMAT),
    prevEnd: prevEnd.format(DATE_FORMAT),
    gran,
  };

  return {
    ...apiParams,
    n: days,
    // i18n key suffix: dashboard.period.comparison.previousDay | previousMonth | previousYear | previousPeriod
    cmp: comparison(days, preset),
    // Whether the period covers at least N days
    has: (minDays) => days >= minDays,
    // parameters the pages send to the API
    apiParams,
  };
}
